// src/components/BilingualNewsEditor.js
import React, { useState, useCallback } from 'react';
import { 
  Plus, Trash2, Languages, RefreshCw, Eye, 
  CheckCircle, AlertCircle, Loader2, Globe, FileText,
  ArrowRight, Save, X, Edit3
} from 'lucide-react';
import { translateMultiNews, translatePostMeta } from '../services/TranslationService';
import './BilingualNewsEditor.css';

// ==================== COMPONENTE DE CARGA DE IMÁGENES ====================
const ImageUploader = ({ onImageUploaded, apiBase }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [inputId] = useState(`image-upload-${Date.now()}-${Math.floor(Math.random() * 1000)}`);

  const uploadToServer = async (file) => {
    const token = localStorage.getItem('blog_token');
    if (!token) throw new Error('No autenticado');

    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${apiBase}/upload/image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) throw new Error('Error al subir imagen');
    const data = await response.json();
    return data.data?.fullUrl || null;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Selecciona una imagen válida');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Máximo 5MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const imageUrl = await uploadToServer(file);
      onImageUploaded(imageUrl);
      e.target.value = '';
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handlePasteURL = () => {
    const url = prompt('Pega la URL de la imagen:', 'https://');
    if (url && url.startsWith('http')) {
      onImageUploaded(url);
    }
  };

  return (
    <div className="bilingual-image-uploader">
      <input
        type="file"
        id={inputId}
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={uploading}
      />
      <label htmlFor={inputId} className="upload-btn">
        {uploading ? '⏳ Subiendo...' : '📤 Subir'}
      </label>
      <button type="button" className="upload-btn upload-btn--secondary" onClick={handlePasteURL}>
        🔗 URL
      </button>
      {error && <span className="upload-error">{error}</span>}
    </div>
  );
};

// ==================== EDITOR DE NOTICIA INDIVIDUAL BILINGÜE ====================
const BilingualNewsItem = ({ 
  index, 
  spanishNews, 
  englishNews, 
  onSpanishChange, 
  onEnglishChange,
  onRemove, 
  canRemove,
  apiBase,
  isTranslated
}) => {
  const [activeTab, setActiveTab] = useState('es');

  const handleFieldChange = (field, value, lang) => {
    if (lang === 'es') {
      onSpanishChange(index, { ...spanishNews, [field]: value });
    } else {
      onEnglishChange(index, { ...englishNews, [field]: value });
    }
  };

  const currentNews = activeTab === 'es' ? spanishNews : englishNews;

  return (
    <div className={`bilingual-news-item ${isTranslated ? 'is-translated' : ''}`}>
      <div className="news-item-header">
        <div className="news-item-number">
          <FileText size={16} />
          <span>Noticia {index + 1}</span>
          {isTranslated && <CheckCircle size={14} className="translated-icon" />}
        </div>
        
        <div className="news-item-tabs">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'es' ? 'active' : ''}`}
            onClick={() => setActiveTab('es')}
          >
            🇪🇸 Español
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'en' ? 'active' : ''} ${!englishNews.title && !englishNews.body ? 'empty' : ''}`}
            onClick={() => setActiveTab('en')}
          >
            🇬🇧 English
            {!englishNews.title && !englishNews.body && <span className="pending-badge">Pendiente</span>}
          </button>
        </div>

        {canRemove && (
          <button
            type="button"
            className="btn-remove-news"
            onClick={() => onRemove(index)}
            title="Eliminar noticia"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div className="news-item-content">
        <div className="form-group">
          <label>
            Título {activeTab === 'es' ? '(Español)' : '(English)'} *
          </label>
          <input
            type="text"
            value={currentNews.title}
            onChange={(e) => handleFieldChange('title', e.target.value, activeTab)}
            placeholder={activeTab === 'es' ? 'Título de la noticia' : 'News title'}
            required={activeTab === 'es'}
          />
        </div>

        <div className="form-group">
          <label>
            Contenido {activeTab === 'es' ? '(Español)' : '(English)'} *
          </label>
          <textarea
            value={currentNews.body}
            onChange={(e) => handleFieldChange('body', e.target.value, activeTab)}
            placeholder={activeTab === 'es' ? 'Contenido de la noticia...' : 'News content...'}
            rows={4}
            required={activeTab === 'es'}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>URL del enlace</label>
            <input
              type="url"
              value={currentNews.url}
              onChange={(e) => handleFieldChange('url', e.target.value, activeTab)}
              placeholder="https://..."
            />
          </div>
          <div className="form-group">
            <label>Texto del enlace</label>
            <input
              type="text"
              value={currentNews.urlText}
              onChange={(e) => handleFieldChange('urlText', e.target.value, activeTab)}
              placeholder={activeTab === 'es' ? 'Leer más' : 'Read more'}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group form-group--image">
            <label>Imagen</label>
            <input
              type="url"
              value={currentNews.imageUrl}
              onChange={(e) => handleFieldChange('imageUrl', e.target.value, activeTab)}
              placeholder="URL de la imagen"
            />
            <ImageUploader 
              onImageUploaded={(url) => handleFieldChange('imageUrl', url, activeTab)} 
              apiBase={apiBase}
            />
          </div>
          <div className="form-group">
            <label>Tamaño</label>
            <select
              value={currentNews.imageSize}
              onChange={(e) => handleFieldChange('imageSize', e.target.value, activeTab)}
            >
              <option value="small">Pequeño</option>
              <option value="medium">Mediano</option>
              <option value="large">Grande</option>
              <option value="full">Completo</option>
            </select>
          </div>
        </div>

        {currentNews.imageUrl && (
          <div className={`image-preview image-preview--${currentNews.imageSize}`}>
            <img src={currentNews.imageUrl} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MODAL DE PREVIEW BILINGÜE ====================
const BilingualPreviewModal = ({ 
  spanishData, 
  englishData, 
  spanishNews, 
  englishNews,
  onClose, 
  onConfirmSave,
  saving
}) => {
  const [viewLang, setViewLang] = useState('both');

  const renderNewsPreview = (news, lang) => (
    <div className="preview-news-list">
      {news.filter(n => n.title && n.body).map((item, idx) => (
        <article key={idx} className="preview-news-article">
          <h4>{item.title}</h4>
          {item.imageUrl && (
            <img src={item.imageUrl} alt={item.title} className={`preview-img--${item.imageSize}`} />
          )}
          <p>{item.body}</p>
          {item.url && (
            <a href={item.url} target="_blank" rel="noopener noreferrer">
              {item.urlText || (lang === 'es' ? 'Leer más' : 'Read more')} →
            </a>
          )}
        </article>
      ))}
    </div>
  );

  return (
    <div className="bilingual-preview-modal">
      <div className="preview-overlay" onClick={onClose}></div>
      <div className="preview-container">
        <div className="preview-header">
          <h2>
            <Eye size={20} />
            Revisión Final - Preview Bilingüe
          </h2>
          <div className="preview-view-toggle">
            <button 
              className={viewLang === 'both' ? 'active' : ''} 
              onClick={() => setViewLang('both')}
            >
              Ambos
            </button>
            <button 
              className={viewLang === 'es' ? 'active' : ''} 
              onClick={() => setViewLang('es')}
            >
              🇪🇸 Solo ESP
            </button>
            <button 
              className={viewLang === 'en' ? 'active' : ''} 
              onClick={() => setViewLang('en')}
            >
              🇬🇧 Solo ENG
            </button>
          </div>
          <button className="preview-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={`preview-content preview-content--${viewLang}`}>
          {(viewLang === 'both' || viewLang === 'es') && (
            <div className="preview-column preview-column--es">
              <div className="preview-lang-badge">
                <span>🇪🇸</span> Versión Español
              </div>
              
              {spanishData.bannerUrl && (
                <div className="preview-banner">
                  <img src={spanishData.bannerUrl} alt={spanishData.title} />
                </div>
              )}

              <div className="preview-meta">
                <h1>{spanishData.title}</h1>
                {spanishData.excerpt && <p className="preview-excerpt">{spanishData.excerpt}</p>}
                <div className="preview-info">
                  <span>👤 {spanishData.authorName}</span>
                  <span>📅 {new Date().toLocaleDateString('es-ES')}</span>
                </div>
              </div>

              {renderNewsPreview(spanishNews, 'es')}
            </div>
          )}

          {(viewLang === 'both' || viewLang === 'en') && (
            <div className="preview-column preview-column--en">
              <div className="preview-lang-badge">
                <span>🇬🇧</span> English Version
              </div>
              
              {englishData.bannerUrl && (
                <div className="preview-banner">
                  <img src={englishData.bannerUrl} alt={englishData.title} />
                </div>
              )}

              <div className="preview-meta">
                <h1>{englishData.title}</h1>
                {englishData.excerpt && <p className="preview-excerpt">{englishData.excerpt}</p>}
                <div className="preview-info">
                  <span>👤 {englishData.authorName}</span>
                  <span>📅 {new Date().toLocaleDateString('en-US')}</span>
                </div>
              </div>

              {renderNewsPreview(englishNews, 'en')}
            </div>
          )}
        </div>

        <div className="preview-actions">
          <button className="btn btn--secondary" onClick={onClose} disabled={saving}>
            <Edit3 size={16} />
            Volver a Editar
          </button>
          <button className="btn btn--primary" onClick={onConfirmSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 size={16} className="spinning" />
                Guardando...
              </>
            ) : (
              <>
                <Save size={16} />
                Publicar en Ambos Idiomas
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================
const BilingualNewsEditor = ({ 
  initialSpanishNews = null,
  initialEnglishNews = null,
  initialFormData = {},
  onSave,
  onCancel,
  loading,
  user,
  apiBase = 'http://localhost:4000/api'
}) => {
  // Estado para los datos del post ESPAÑOL
  const [spanishFormData, setSpanishFormData] = useState({
    title: initialFormData.title || getDefaultTitle('es'),
    excerpt: initialFormData.excerpt || '',
    bannerUrl: initialFormData.bannerUrl || '',
    authorName: initialFormData.authorName || user?.username || '',
    status: initialFormData.status || 'draft',
    featured: initialFormData.featured || false,
  });

  // Estado para los datos del post INGLÉS (banner separado)
  const [englishFormData, setEnglishFormData] = useState({
    title: '',
    excerpt: '',
    bannerUrl: '',
    authorName: initialFormData.authorName || user?.username || '',
    status: initialFormData.status || 'draft',
    featured: initialFormData.featured || false,
  });

  // Estado para las noticias
  const [spanishNews, setSpanishNews] = useState(
    initialSpanishNews || [createEmptyNews()]
  );
  const [englishNews, setEnglishNews] = useState(
    initialEnglishNews || [createEmptyNews()]
  );

  // Estados de UI
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState({ current: 0, total: 0, status: '' });
  const [isTranslated, setIsTranslated] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState(null);

  function createEmptyNews() {
    return {
      title: '',
      body: '',
      url: '',
      urlText: '',
      imageUrl: '',
      imageSize: 'medium',
    };
  }

  function getDefaultTitle(lang) {
    const today = new Date();
    if (lang === 'es') {
      return `WhataNews | ${today.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`;
    }
    return `WhataNews | ${today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`;
  }

  const handleSpanishNewsChange = (index, updatedNews) => {
    const newNews = [...spanishNews];
    newNews[index] = updatedNews;
    setSpanishNews(newNews);
    setIsTranslated(false);
  };

  const handleEnglishNewsChange = (index, updatedNews) => {
    const newNews = [...englishNews];
    newNews[index] = updatedNews;
    setEnglishNews(newNews);
  };

  const handleAddNews = () => {
    if (spanishNews.length < 5) {
      setSpanishNews([...spanishNews, createEmptyNews()]);
      setEnglishNews([...englishNews, createEmptyNews()]);
      setIsTranslated(false);
    }
  };

  const handleRemoveNews = (index) => {
    if (spanishNews.length > 1) {
      setSpanishNews(spanishNews.filter((_, i) => i !== index));
      setEnglishNews(englishNews.filter((_, i) => i !== index));
      setIsTranslated(false);
    }
  };

  // ==================== TRADUCCIÓN AUTOMÁTICA ====================
  const handleTranslate = async () => {
    setIsTranslating(true);
    setError(null);
    setTranslationProgress({ current: 0, total: 0, status: 'Iniciando traducción...' });

    try {
      // 1. Traducir título y extracto
      setTranslationProgress({ current: 0, total: 0, status: 'Traduciendo título y extracto...' });
      
      const translatedMeta = await translatePostMeta(spanishFormData, 'es', 'en');
      
      setEnglishFormData(prev => ({
        ...prev,
        title: translatedMeta.title || prev.title,
        excerpt: translatedMeta.excerpt || prev.excerpt,
        // NO copiamos el banner - se mantiene independiente
      }));

      // 2. Traducir noticias
      const translatedNews = await translateMultiNews(
        spanishNews,
        'es',
        'en',
        (current, total, status) => {
          setTranslationProgress({ current, total, status });
        }
      );

      // Preservar URLs e imágenes del español
      const finalEnglishNews = translatedNews.map((news, idx) => ({
        ...news,
        url: spanishNews[idx].url,
        imageUrl: spanishNews[idx].imageUrl,
        imageSize: spanishNews[idx].imageSize,
      }));

      setEnglishNews(finalEnglishNews);
      setIsTranslated(true);
      setTranslationProgress({ current: 0, total: 0, status: '' });

    } catch (err) {
      console.error('Translation error:', err);
      setError('Error en la traducción. Por favor, intenta de nuevo.');
    } finally {
      setIsTranslating(false);
    }
  };

  // ==================== VALIDACIÓN Y GUARDADO ====================
  const handleSubmit = (e) => {
    e.preventDefault();

    const hasSpanishContent = spanishNews.some(n => n.title && n.body);
    if (!hasSpanishContent) {
      setError('Debes completar al menos una noticia con título y contenido en español');
      return;
    }

    if (!isTranslated) {
      setError('Debes traducir las noticias antes de publicar. Haz clic en "Traducir al Inglés"');
      return;
    }

    // Validar que el banner inglés esté configurado
    if (!englishFormData.bannerUrl) {
      setError('Debes subir un banner para la versión en inglés');
      return;
    }

    setShowPreview(true);
  };

  const handleConfirmSave = async () => {
    const spanishPost = {
      ...spanishFormData,
      language: 'es',
      templateType: 'multiNews',
      templateContent: JSON.stringify(spanishNews),
      published: spanishFormData.status === 'published',
    };

    const englishPost = {
      ...englishFormData,
      language: 'en',
      templateType: 'multiNews',
      templateContent: JSON.stringify(englishNews),
      published: englishFormData.status === 'published',
    };

    spanishNews.forEach((news, idx) => {
      const num = idx + 1;
      spanishPost[`news${num}Title`] = news.title || null;
      spanishPost[`news${num}Body`] = news.body || null;
      spanishPost[`news${num}Url`] = news.url || null;
      spanishPost[`news${num}UrlText`] = news.urlText || null;
      spanishPost[`news${num}ImageUrl`] = news.imageUrl || null;
      spanishPost[`news${num}ImageSize`] = news.imageSize || 'medium';
    });

    englishNews.forEach((news, idx) => {
      const num = idx + 1;
      englishPost[`news${num}Title`] = news.title || null;
      englishPost[`news${num}Body`] = news.body || null;
      englishPost[`news${num}Url`] = news.url || null;
      englishPost[`news${num}UrlText`] = news.urlText || null;
      englishPost[`news${num}ImageUrl`] = news.imageUrl || null;
      englishPost[`news${num}ImageSize`] = news.imageSize || 'medium';
    });

    await onSave({ spanishPost, englishPost });
    setShowPreview(false);
  };

  return (
    <div className="bilingual-news-editor">
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div className="editor-header">
          <div className="editor-title">
            <Globe size={24} />
            <div>
              <h2>Editor Bilingüe Multi-Noticias</h2>
              <p>Escribe en español y traduce automáticamente al inglés</p>
            </div>
          </div>
          <div className="editor-actions">
            <button type="button" className="btn btn--secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn btn--primary" disabled={loading || !isTranslated}>
              {loading ? 'Guardando...' : 'Revisar y Publicar'}
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="editor-error">
            <AlertCircle size={18} />
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Workflow indicator */}
        <div className="workflow-steps">
          <div className={`workflow-step ${spanishNews.some(n => n.title && n.body) ? 'completed' : 'active'}`}>
            <span className="step-number">1</span>
            <span className="step-label">Escribe en Español</span>
          </div>
          <ArrowRight size={16} className="workflow-arrow" />
          <div className={`workflow-step ${isTranslated ? 'completed' : spanishNews.some(n => n.title && n.body) ? 'active' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Traduce al Inglés</span>
          </div>
          <ArrowRight size={16} className="workflow-arrow" />
          <div className={`workflow-step ${isTranslated ? 'active' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Revisa y Publica</span>
          </div>
        </div>

        {/* Main content grid */}
        <div className="editor-grid">
          {/* Left: Post metadata */}
          <div className="editor-sidebar">
            {/* ==================== SECCIÓN ESPAÑOL ==================== */}
            <div className="sidebar-section">
              <h3>🇪🇸 Datos del Post (Español)</h3>
              
              <div className="form-group">
                <label>Título Principal *</label>
                <input
                  type="text"
                  value={spanishFormData.title}
                  onChange={(e) => {
                    setSpanishFormData(prev => ({ ...prev, title: e.target.value }));
                    setIsTranslated(false);
                  }}
                  placeholder="WhataNews | Fecha"
                  required
                />
              </div>

              <div className="form-group">
                <label>Extracto</label>
                <textarea
                  value={spanishFormData.excerpt}
                  onChange={(e) => {
                    setSpanishFormData(prev => ({ ...prev, excerpt: e.target.value }));
                    setIsTranslated(false);
                  }}
                  placeholder="Breve descripción..."
                  rows={2}
                  maxLength={500}
                />
              </div>

              <div className="form-group">
                <label>Banner Español 🇪🇸</label>
                <input
                  type="url"
                  value={spanishFormData.bannerUrl}
                  onChange={(e) => setSpanishFormData(prev => ({ ...prev, bannerUrl: e.target.value }))}
                  placeholder="URL del banner español"
                />
                <ImageUploader
                  onImageUploaded={(url) => setSpanishFormData(prev => ({ ...prev, bannerUrl: url }))}
                  apiBase={apiBase}
                />
                {spanishFormData.bannerUrl && (
                  <div className="banner-preview">
                    <img src={spanishFormData.bannerUrl} alt="Banner ESP" />
                  </div>
                )}
              </div>
            </div>

            {/* ==================== SECCIÓN INGLÉS ==================== */}
            <div className="sidebar-section">
              <h3>🇬🇧 Datos del Post (English)</h3>
              
              <div className="form-group">
                <label>Title (traducido)</label>
                <input
                  type="text"
                  value={englishFormData.title}
                  onChange={(e) => setEnglishFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={isTranslated ? '' : '← Primero traduce'}
                  className={!isTranslated ? 'input-pending' : ''}
                />
              </div>

              <div className="form-group">
                <label>Excerpt (traducido)</label>
                <textarea
                  value={englishFormData.excerpt}
                  onChange={(e) => setEnglishFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                  placeholder={isTranslated ? '' : '← Primero traduce'}
                  rows={2}
                  className={!isTranslated ? 'input-pending' : ''}
                />
              </div>

              <div className="form-group">
                <label>Banner English 🇬🇧</label>
                <input
                  type="url"
                  value={englishFormData.bannerUrl}
                  onChange={(e) => setEnglishFormData(prev => ({ ...prev, bannerUrl: e.target.value }))}
                  placeholder="URL del banner inglés"
                />
                <ImageUploader
                  onImageUploaded={(url) => setEnglishFormData(prev => ({ ...prev, bannerUrl: url }))}
                  apiBase={apiBase}
                />
                {englishFormData.bannerUrl && (
                  <div className="banner-preview">
                    <img src={englishFormData.bannerUrl} alt="Banner ENG" />
                  </div>
                )}
                {!englishFormData.bannerUrl && (
                  <div className="banner-warning">
                    ⚠️ Sube un banner para la versión inglesa
                  </div>
                )}
              </div>
            </div>

            {/* ==================== CONFIGURACIÓN COMÚN ==================== */}
            <div className="sidebar-section">
              <h3>⚙️ Configuración</h3>

              <div className="form-group">
                <label>Autor</label>
                <input
                  type="text"
                  value={spanishFormData.authorName}
                  disabled
                  className="input-disabled"
                />
              </div>

              <div className="form-group">
                <label>Estado</label>
                <select
                  value={spanishFormData.status}
                  onChange={(e) => {
                    setSpanishFormData(prev => ({ ...prev, status: e.target.value }));
                    setEnglishFormData(prev => ({ ...prev, status: e.target.value }));
                  }}
                >
                  <option value="draft">Borrador</option>
                  <option value="published">Publicado</option>
                </select>
              </div>

              <div className="form-group form-group--checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={spanishFormData.featured}
                    onChange={(e) => {
                      setSpanishFormData(prev => ({ ...prev, featured: e.target.checked }));
                      setEnglishFormData(prev => ({ ...prev, featured: e.target.checked }));
                    }}
                  />
                  <span>Artículo destacado</span>
                </label>
              </div>
            </div>

            {/* Translation button */}
            <div className="translation-section">
              <button
                type="button"
                className={`btn btn--translate ${isTranslating ? 'translating' : ''} ${isTranslated ? 'translated' : ''}`}
                onClick={handleTranslate}
                disabled={isTranslating || !spanishNews.some(n => n.title && n.body)}
              >
                {isTranslating ? (
                  <>
                    <Loader2 size={18} className="spinning" />
                    Traduciendo...
                  </>
                ) : isTranslated ? (
                  <>
                    <CheckCircle size={18} />
                    Traducido ✓
                  </>
                ) : (
                  <>
                    <Languages size={18} />
                    Traducir al Inglés
                  </>
                )}
              </button>

              {isTranslating && (
                <div className="translation-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${translationProgress.total ? (translationProgress.current / translationProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="progress-text">{translationProgress.status}</span>
                </div>
              )}

              {isTranslated && (
                <button
                  type="button"
                  className="btn btn--refresh"
                  onClick={handleTranslate}
                  disabled={isTranslating}
                >
                  <RefreshCw size={14} />
                  Re-traducir
                </button>
              )}
            </div>
          </div>

          {/* Right: News items */}
          <div className="editor-main">
            <div className="news-header">
              <h3>
                <FileText size={20} />
                Noticias ({spanishNews.length}/5)
              </h3>
              {spanishNews.length < 5 && (
                <button type="button" className="btn btn--add" onClick={handleAddNews}>
                  <Plus size={16} />
                  Añadir Noticia
                </button>
              )}
            </div>

            <div className="news-list">
              {spanishNews.map((news, index) => (
                <BilingualNewsItem
                  key={index}
                  index={index}
                  spanishNews={news}
                  englishNews={englishNews[index] || createEmptyNews()}
                  onSpanishChange={handleSpanishNewsChange}
                  onEnglishChange={handleEnglishNewsChange}
                  onRemove={handleRemoveNews}
                  canRemove={spanishNews.length > 1}
                  apiBase={apiBase}
                  isTranslated={isTranslated && englishNews[index]?.title && englishNews[index]?.body}
                />
              ))}
            </div>
          </div>
        </div>
      </form>

      {/* Preview Modal */}
      {showPreview && (
        <BilingualPreviewModal
          spanishData={spanishFormData}
          englishData={englishFormData}
          spanishNews={spanishNews}
          englishNews={englishNews}
          onClose={() => setShowPreview(false)}
          onConfirmSave={handleConfirmSave}
          saving={loading}
        />
      )}
    </div>
  );
};

export default BilingualNewsEditor;